import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

interface WorkflowItem {
  id: string;
  name: string;
  description: string | null;
  definition: {
    trigger: { type: string; expression?: string; timezone?: string };
    steps: { name: string; type: string }[];
    delivery: { type: string };
  };
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface WorkflowRun {
  id: string;
  workflowId: string;
  status: string;
  triggeredAt: string;
  completedAt: string | null;
  totalCostUsd: string | null;
  error: string | null;
  durationMs: number | null;
}

export function useWorkflows() {
  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch("/api/workflows");
      if (res.ok) {
        setWorkflows((await res.json()) as WorkflowItem[]);
      }
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = useCallback(async (id: string, enabled: boolean) => {
    const res = await apiFetch(`/api/workflows/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    });
    if (res.ok) {
      setWorkflows((prev) =>
        prev.map((w) => (w.id === id ? { ...w, enabled } : w)),
      );
    }
  }, []);

  const remove = useCallback(async (id: string) => {
    const res = await apiFetch(`/api/workflows/${id}`, { method: "DELETE" });
    if (res.ok) {
      setWorkflows((prev) => prev.filter((w) => w.id !== id));
    }
  }, []);

  const getRuns = useCallback(async (id: string): Promise<WorkflowRun[]> => {
    const res = await apiFetch(`/api/workflows/${id}/runs`);
    if (res.ok) {
      return (await res.json()) as WorkflowRun[];
    }
    return [];
  }, []);

  return { workflows, loading, refresh, toggle, remove, getRuns };
}

export type { WorkflowItem, WorkflowRun };
